import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Select,
    Statistic,
    Row,
    Col,
    Descriptions,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DollarOutlined,
    ClockCircleOutlined,
    CalculatorOutlined,
    PieChartOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { Title } = Typography;

const CostAnalysis = () => {
    const [blocks, setBlocks] = useState([]);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [costs, setCosts] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchBlocks();
    }, []);

    const fetchBlocks = async () => {
        try {
            const response = await api.get('/blocks');
            setBlocks(response.data);
            if (response.data.length > 0 && !selectedBlock) {
                setSelectedBlock(response.data[0].id);
            }
        } catch (error) {
            message.error('Failed to fetch blocks');
        }
    };

    useEffect(() => {
        if (selectedBlock) {
            fetchBlockCosts(selectedBlock);
        }
    }, [selectedBlock]);

    const fetchBlockCosts = async (blockId) => {
        setLoading(true);
        try {
            const response = await api.get(`/blocks/${blockId}/costs`);
            setCosts(response.data);
        } catch (error) {
            message.error('Failed to fetch costs');
        } finally {
            setLoading(false);
        }
    };

    const costColumns = [
        {
            title: 'Cost Type',
            dataIndex: 'cost_type',
            key: 'cost_type',
            render: (text) => {
                const names = {
                    'loading_soft': 'Soft Soil Loading',
                    'loading_hard': 'Hard Soil Loading',
                    'loading_coal': 'Coal Loading',
                    'drilling_blasting': 'Drill & Blast',
                    'crushing': 'Crushing & Screening',
                    'logistics': 'Logistics',
                    'fixed': 'Fixed Costs'
                };
                return names[text] || text;
            }
        },
        {
            title: 'Total Hours',
            dataIndex: 'total_hours',
            key: 'total_hours',
            render: (value) => value?.toFixed(2) || '0.00'
        },
        {
            title: 'Total Cost (ZAR)',
            dataIndex: 'total_cost',
            key: 'total_cost',
            render: (value) => value?.toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR',
                minimumFractionDigits: 2
            }) || 'R 0.00'
        },
        {
            title: 'Cost per m³',
            dataIndex: 'cost_per_m3',
            key: 'cost_per_m3',
            render: (value) => value?.toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR',
                minimumFractionDigits: 2
            }) || 'R 0.00'
        },
        {
            title: 'Cost per Ton',
            dataIndex: 'cost_per_ton',
            key: 'cost_per_ton',
            render: (value) => value?.toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR',
                minimumFractionDigits: 2
            }) || 'R 0.00'
        }
    ];

    const selectedBlockData = blocks.find(b => b.id === selectedBlock);

    return (
        <div>
            <Card>
                <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={4}>Cost Analysis</Title>
                    </Col>
                    <Col>
                        <Select
                            value={selectedBlock}
                            onChange={setSelectedBlock}
                            style={{ width: 300 }}
                            placeholder="Select a block"
                        >
                            {blocks.map(block => (
                                <Option key={block.id} value={block.id}>
                                    {block.block_code} - {block.block_name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                {selectedBlockData && (
                    <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
                        <Descriptions.Item label="Block Name">
                            {selectedBlockData.block_name}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={
                                selectedBlockData.status === 'in_progress' ? 'green' :
                                selectedBlockData.status === 'completed' ? 'purple' :
                                selectedBlockData.status === 'on_hold' ? 'orange' : 'blue'
                            }>
                                {selectedBlockData.status.replace('_', ' ').toUpperCase()}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Total Soft Soil">
                            {selectedBlockData.total_soft_soil?.toLocaleString()} m³
                        </Descriptions.Item>
                        <Descriptions.Item label="Total Coal">
                            {selectedBlockData.total_coal?.toLocaleString()} tons
                        </Descriptions.Item>
                        <Descriptions.Item label="Soft Soil Progress">
                            <span>
                                {selectedBlockData.soft_soil_moved?.toLocaleString()} / 
                                {selectedBlockData.total_soft_soil?.toLocaleString()} m³
                                ({((selectedBlockData.soft_progress || 0)).toFixed(1)}%)
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Coal Progress">
                            <span>
                                {selectedBlockData.coal_moved?.toLocaleString()} / 
                                {selectedBlockData.total_coal?.toLocaleString()} tons
                                ({((selectedBlockData.coal_progress || 0)).toFixed(1)}%)
                            </span>
                        </Descriptions.Item>
                    </Descriptions>
                )}

                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Estimated Total Cost"
                                value={costs.reduce((sum, cost) => sum + (cost.total_cost || 0), 0)}
                                prefix={<DollarOutlined />}
                                valueStyle={{ color: '#cf1322' }}
                                formatter={value => 
                                    Number(value).toLocaleString('en-ZA', {
                                        style: 'currency',
                                        currency: 'ZAR'
                                    })
                                }
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Hours"
                                value={costs.reduce((sum, cost) => sum + (cost.total_hours || 0), 0)}
                                prefix={<ClockCircleOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                                suffix="hrs"
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Avg Cost per m³"
                                value={costs.reduce((sum, cost) => sum + (cost.cost_per_m3 || 0), 0) / Math.max(costs.length, 1)}
                                prefix={<CalculatorOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                                formatter={value => 
                                    Number(value).toLocaleString('en-ZA', {
                                        style: 'currency',
                                        currency: 'ZAR',
                                        minimumFractionDigits: 2
                                    })
                                }
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Avg Cost per Ton"
                                value={costs.reduce((sum, cost) => sum + (cost.cost_per_ton || 0), 0) / Math.max(costs.length, 1)}
                                prefix={<PieChartOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                                formatter={value => 
                                    Number(value).toLocaleString('en-ZA', {
                                        style: 'currency',
                                        currency: 'ZAR',
                                        minimumFractionDigits: 2
                                    })
                                }
                            />
                        </Card>
                    </Col>
                </Row>

                <Table
                    columns={costColumns}
                    dataSource={costs}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                />
            </Card>
        </div>
    );
};

export default CostAnalysis;